import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Edit, 
  Trash2, 
  Eye, 
  Award, 
  Calendar, 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  FileText, 
  UserCheck, 
  BarChart2,
  X,
  ChevronRight,
  Printer,
  Share2,
  Mail,
  Settings,
  RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Mock data
const mockData = {
  certificates: [
    {
      id: 1,
      certificateId: "CERT-001-2024",
      learnerName: "Ahmed Al-Rashid",
      learnerEmail: "ahmed@company.com",
      courseName: "Food Safety Fundamentals",
      issueDate: "2024-03-10",
      expiryDate: "2025-03-10",
      score: 92,
      status: "valid",
      template: "Standard Certificate",
      issuedBy: "Sarah Johnson",
      department: "Kitchen",
      downloadUrl: "/certificates/cert-001-2024.pdf",
      verificationUrl: "https://verify.company.com/cert-001-2024"
    },
    {
      id: 2,
      certificateId: "CERT-002-2024",
      learnerName: "Fatima Hassan",
      learnerEmail: "fatima@company.com",
      courseName: "Customer Service Excellence",
      issueDate: "2024-03-08",
      expiryDate: "2025-03-08",
      score: 88,
      status: "valid",
      template: "Premium Certificate",
      issuedBy: "Mike Chen",
      department: "Service",
      downloadUrl: "/certificates/cert-002-2024.pdf",
      verificationUrl: "https://verify.company.com/cert-002-2024"
    },
    {
      id: 3,
      certificateId: "CERT-003-2024",
      learnerName: "Omar Khalil",
      learnerEmail: "omar@company.com",
      courseName: "Inventory Management",
      issueDate: "2024-03-05",
      expiryDate: "2025-03-05",
      score: 85,
      status: "valid",
      template: "Standard Certificate",
      issuedBy: "Lisa Rodriguez",
      department: "Management",
      downloadUrl: "/certificates/cert-003-2024.pdf",
      verificationUrl: "https://verify.company.com/cert-003-2024"
    },
    {
      id: 4,
      certificateId: "CERT-004-2024",
      learnerName: "Layla Al-Mansouri",
      learnerEmail: "layla@company.com",
      courseName: "Food Safety Fundamentals",
      issueDate: "2024-02-28",
      expiryDate: "2025-02-28",
      score: 80,
      status: "valid",
      template: "Standard Certificate",
      issuedBy: "Sarah Johnson",
      department: "Kitchen",
      downloadUrl: "/certificates/cert-004-2024.pdf",
      verificationUrl: "https://verify.company.com/cert-004-2024"
    },
    {
      id: 5,
      certificateId: "CERT-005-2024",
      learnerName: "Khalid Al-Zahrani",
      learnerEmail: "khalid@company.com",
      courseName: "Leadership Skills",
      issueDate: "2024-02-20",
      expiryDate: "2025-02-20",
      score: 90,
      status: "expired",
      template: "Premium Certificate",
      issuedBy: "David Wilson",
      department: "Management",
      downloadUrl: "/certificates/cert-005-2024.pdf",
      verificationUrl: "https://verify.company.com/cert-005-2024"
    }
  ],
  learners: [
    { id: 1, name: "Ahmed Al-Rashid", email: "ahmed@company.com", department: "Kitchen" },
    { id: 2, name: "Fatima Hassan", email: "fatima@company.com", department: "Service" },
    { id: 3, name: "Omar Khalil", email: "omar@company.com", department: "Management" },
    { id: 4, name: "Layla Al-Mansouri", email: "layla@company.com", department: "Kitchen" }
  ],
  courses: [
    { id: 1, name: "Food Safety Fundamentals" },
    { id: 2, name: "Customer Service Excellence" },
    { id: 3, name: "Inventory Management" },
    { id: 4, name: "Leadership Skills" }
  ],
  templates: [
    { id: 1, name: "Standard Certificate", description: "Basic certificate template" },
    { id: 2, name: "Premium Certificate", description: "Advanced certificate with logo" },
    { id: 3, name: "Custom Certificate", description: "Fully customizable template" }
  ]
};

const CertificateManagement = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState(mockData.certificates);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTemplate, setFilterTemplate] = useState('all');
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'certificates', label: 'Certificates', icon: Award },
    { id: 'templates', label: 'Templates', icon: FileText }
  ];

  // Filter certificates based on search and filters
  const filteredCertificates = certificates.filter(certificate => {
    const matchesSearch = certificate.learnerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         certificate.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         certificate.certificateId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || certificate.status === filterStatus;
    const matchesTemplate = filterTemplate === 'all' || certificate.template === filterTemplate;
    return matchesSearch && matchesStatus && matchesTemplate;
  });

  const handleIssueCertificate = (certificateData) => {
    const newCertificate = {
      id: Date.now(),
      certificateId: `CERT-${String(Date.now()).slice(-6)}-${new Date().getFullYear()}`,
      ...certificateData,
      issueDate: new Date().toISOString().split('T')[0],
      expiryDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: "valid",
      issuedBy: "Current User",
      downloadUrl: `/certificates/${String(Date.now()).slice(-6)}-${new Date().getFullYear()}.pdf`,
      verificationUrl: `https://verify.company.com/${String(Date.now()).slice(-6)}-${new Date().getFullYear()}`
    };
    setCertificates([newCertificate, ...certificates]);
    setShowIssueModal(false);
  };

  const handleDeleteCertificate = (certificateId) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      setCertificates(certificates.filter(cert => cert.id !== certificateId));
    }
  };

  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setShowViewModal(true);
  };

  const handleDownloadCertificate = (certificate) => {
    alert(`Downloading certificate: ${certificate.certificateId}`);
    // In a real app, this would trigger a download
  };

  const handlePrintCertificate = (certificate) => {
    alert(`Printing certificate: ${certificate.certificateId}`);
    // In a real app, this would open print dialog
  };

  const handleShareCertificate = (certificate) => {
    alert(`Sharing certificate: ${certificate.certificateId}`);
    // In a real app, this would open share dialog
  };

  const OverviewContent = () => {
    const stats = {
      totalCertificates: certificates.length,
      validCertificates: certificates.filter(c => c.status === 'valid').length,
      expiredCertificates: certificates.filter(c => c.status === 'expired').length,
      avgScore: Math.round(certificates.reduce((acc, c) => acc + c.score, 0) / certificates.length)
    };

    const monthlyData = [
      { month: 'Jan', issued: 12, expired: 2 },
      { month: 'Feb', issued: 18, expired: 3 },
      { month: 'Mar', issued: 15, expired: 1 },
      { month: 'Apr', issued: 22, expired: 4 },
      { month: 'May', issued: 25, expired: 2 },
      { month: 'Jun', issued: 28, expired: 5 }
    ];

    const templateUsage = mockData.templates.map(template => ({
      template: template.name,
      count: certificates.filter(c => c.template === template.name).length
    }));

    return (
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Certificates</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.totalCertificates}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Valid Certificates</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.validCertificates}</h3>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Expired Certificates</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.expiredCertificates}</h3>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Average Score</p>
                <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.avgScore}%</h3>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart2 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold mb-4">Certificate Trends</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="issued" stroke="#8B5CF6" strokeWidth={2} />
                <Line type="monotone" dataKey="expired" stroke="#EF4444" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-6 shadow-sm"
          >
            <h3 className="text-lg font-semibold mb-4">Template Usage</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={templateUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="template" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#8B5CF6" />
              </BarChart>
            </ResponsiveContainer>
          </motion.div>
        </div>
      </div>
    );
  };

  const CertificatesContent = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search certificates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="valid">Valid</option>
          <option value="expired">Expired</option>
        </select>
        <select
          value={filterTemplate}
          onChange={(e) => setFilterTemplate(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="all">All Templates</option>
          {mockData.templates.map(template => (
            <option key={template.id} value={template.name}>{template.name}</option>
          ))}
        </select>
        <button
          onClick={() => setShowIssueModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Issue Certificate
        </button>
      </div>

      {/* Certificates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCertificates.map((certificate) => (
          <motion.div
            key={certificate.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{certificate.learnerName}</h3>
                <p className="text-sm text-gray-500">{certificate.courseName}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewCertificate(certificate)}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteCertificate(certificate.id)}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-500">Score</p>
                <p className="font-medium text-green-600">{certificate.score}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  certificate.status === 'valid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {certificate.status}
                </span>
              </div>
            </div>
            
            <div className="text-xs text-gray-500 space-y-1 mb-4">
              <p>Issued: {certificate.issueDate}</p>
              <p>Expires: {certificate.expiryDate}</p>
              <p>ID: {certificate.certificateId}</p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleDownloadCertificate(certificate)}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Download className="w-3 h-3" />
                Download
              </button>
              <button
                onClick={() => handlePrintCertificate(certificate)}
                className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Printer className="w-3 h-3" />
              </button>
              <button
                onClick={() => handleShareCertificate(certificate)}
                className="flex items-center justify-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Share2 className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const TemplatesContent = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {mockData.templates.map((template) => (
          <motion.div
            key={template.id}
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{template.name}</h3>
                <p className="text-sm text-gray-500">{template.description}</p>
              </div>
              <div className="flex gap-2">
                <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                  <Edit className="w-4 h-4" />
                </button>
                <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-500">Usage</p>
              <p className="font-medium">{certificates.filter(c => c.template === template.name).length} certificates</p>
            </div>

            <div className="flex gap-2">
              <button className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm">
                Edit Template
              </button>
              <button className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                <Eye className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const IssueCertificateModal = ({ isOpen, onClose, onIssue }) => {
    const [formData, setFormData] = useState({
      learnerId: '',
      courseId: '',
      score: '',
      templateId: '',
      validityMonths: 12
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      const learner = mockData.learners.find(l => l.id === parseInt(formData.learnerId));
      const course = mockData.courses.find(c => c.id === parseInt(formData.courseId));
      const template = mockData.templates.find(t => t.id === parseInt(formData.templateId));
      
      onIssue({
        learnerName: learner.name,
        learnerEmail: learner.email,
        courseName: course.name,
        score: parseInt(formData.score),
        template: template.name,
        department: learner.department
      });
      
      setFormData({
        learnerId: '',
        courseId: '',
        score: '',
        templateId: '',
        validityMonths: 12
      });
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl w-full max-w-md shadow-xl m-4"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Issue Certificate</h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learner *
                </label>
                <select
                  value={formData.learnerId}
                  onChange={(e) => setFormData({ ...formData, learnerId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select learner</option>
                  {mockData.learners.map(learner => (
                    <option key={learner.id} value={learner.id}>
                      {learner.name} - {learner.department}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course *
                </label>
                <select
                  value={formData.courseId}
                  onChange={(e) => setFormData({ ...formData, courseId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select course</option>
                  {mockData.courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Score (%) *
                </label>
                <input
                  type="number"
                  value={formData.score}
                  onChange={(e) => setFormData({ ...formData, score: e.target.value })}
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template *
                </label>
                <select
                  value={formData.templateId}
                  onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Select template</option>
                  {mockData.templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} - {template.description}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Validity (months)
                </label>
                <input
                  type="number"
                  value={formData.validityMonths}
                  onChange={(e) => setFormData({ ...formData, validityMonths: parseInt(e.target.value) })}
                  min="1"
                  max="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Issue Certificate
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    );
  };

  const ViewCertificateModal = ({ isOpen, onClose, certificate }) => {
    if (!isOpen || !certificate) return null;

    return (
      <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl m-4"
        >
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Certificate Details</h3>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Certificate Preview */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="text-lg font-semibold mb-4">Certificate Preview</h4>
                <div className="bg-white rounded-lg p-6 border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <Award className="w-16 h-16 text-purple-600 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Certificate of Completion</h2>
                    <p className="text-gray-600 mb-6">This is to certify that</p>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{certificate.learnerName}</h3>
                    <p className="text-gray-600 mb-4">has successfully completed</p>
                    <h4 className="text-lg font-medium text-gray-900 mb-4">{certificate.courseName}</h4>
                    <div className="flex justify-center items-center gap-8 mb-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Score</p>
                        <p className="text-lg font-semibold text-green-600">{certificate.score}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-gray-500">Issue Date</p>
                        <p className="text-lg font-semibold">{certificate.issueDate}</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">Certificate ID: {certificate.certificateId}</p>
                  </div>
                </div>
              </div>

              {/* Certificate Details */}
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-lg font-semibold mb-4">Certificate Information</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Certificate ID</p>
                      <p className="font-medium">{certificate.certificateId}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Learner</p>
                      <p className="font-medium">{certificate.learnerName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Course</p>
                      <p className="font-medium">{certificate.courseName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Score</p>
                      <p className="font-medium text-green-600">{certificate.score}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        certificate.status === 'valid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {certificate.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Issue Date</p>
                      <p className="font-medium">{certificate.issueDate}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Expiry Date</p>
                      <p className="font-medium">{certificate.expiryDate}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Template</p>
                      <p className="font-medium">{certificate.template}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Issued By</p>
                      <p className="font-medium">{certificate.issuedBy}</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleDownloadCertificate(certificate)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <button
                    onClick={() => handlePrintCertificate(certificate)}
                    className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShareCertificate(certificate)}
                    className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewContent />;
      case 'certificates':
        return <CertificatesContent />;
      case 'templates':
        return <TemplatesContent />;
      default:
        return <OverviewContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/bloom"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Bloom
          </Link>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Certificate Management</h1>
            <p className="text-gray-500">Issue, track, and manage digital certificates</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => alert('Exporting certificate data...')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={() => setShowIssueModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Issue Certificate
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        <AnimatePresence mode="wait">
          {renderTabContent()}
        </AnimatePresence>
      </div>

      {/* Modals */}
      <IssueCertificateModal
        isOpen={showIssueModal}
        onClose={() => setShowIssueModal(false)}
        onIssue={handleIssueCertificate}
      />

      <ViewCertificateModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        certificate={selectedCertificate}
      />
    </div>
  );
};

export default CertificateManagement;
